// src/components/ShareAnalyticsDrawer.tsx
import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tabs,
  Empty,
  Spin,
  message,
  Tag,
  Space
} from 'antd';
import {
  EyeOutlined,
  UserOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  MailOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { useCustom } from '@refinedev/core';
import type { ShareAnalytics, ShareViewerDetail, ShareTimelineDataPoint } from '../types/share';

const { TabPane } = Tabs;

interface ShareAnalyticsDrawerProps {
  visible: boolean;
  onClose: () => void;
  shareToken: string;
  videoTitle?: string;
}

export const ShareAnalyticsDrawer: React.FC<ShareAnalyticsDrawerProps> = ({
  visible,
  onClose,
  shareToken,
  videoTitle
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch analytics data
  const { data, isLoading, isError } = useCustom<ShareAnalytics>({
    url: `/share-analytics/${shareToken}`,
    method: 'get',
    queryOptions: {
      enabled: visible && !!shareToken,
    },
  });

  // DEBUG: Log response structure
  console.log('=== ANALYTICS RESPONSE ===');
  console.log('Full data:', data);
  console.log('data.data:', data?.data);
  console.log('data.data.data:', (data as any)?.data?.data);
  console.log('==========================');

  // Extract analytics - check if dataProvider wraps response
  const analytics: ShareAnalytics | undefined = (data as any)?.data?.data || data?.data;

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${minutes}m ${secs}s`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const viewersColumns = [
    {
      title: 'Email',
      dataIndex: 'viewerEmail',
      key: 'email',
      render: (email: string | null) => (
        <Space>
          <MailOutlined style={{ color: '#999' }} />
          <span>{email || 'Anonymous'}</span>
        </Space>
      ),
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      render: (location: string) => (
        <Space>
          <EnvironmentOutlined style={{ color: '#999' }} />
          <span>{location || 'Unknown'}</span>
        </Space>
      ),
    },
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp: Date) => (
        <Space>
          <CalendarOutlined style={{ color: '#999' }} />
          <span>{formatDate(timestamp.toString())}</span>
        </Space>
      ),
    },
    {
      title: 'Watch Time',
      dataIndex: 'videoWatchTime',
      key: 'watchTime',
      render: (time: number) => formatDuration(time),
    },
    {
      title: 'Completion',
      dataIndex: 'completionPercent',
      key: 'completion',
      render: (percent: number) => (
        <Tag color={percent >= 75 ? 'green' : percent >= 50 ? 'orange' : 'red'}>
          {Math.round(percent)}%
        </Tag>
      ),
    },
  ];

  // Mobile-friendly columns
  const mobileViewersColumns = [
    {
      title: 'Viewer',
      key: 'viewer',
      render: (record: ShareViewerDetail) => (
        <div>
          <div style={{ fontWeight: 500, marginBottom: '4px' }}>
            {record.viewerEmail || 'Anonymous'}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            <div>{record.location || 'Unknown location'}</div>
            <div>{formatDate(record.timestamp.toString())}</div>
          </div>
          <div style={{ marginTop: '8px' }}>
            <Tag color={record.completionPercent >= 75 ? 'green' : record.completionPercent >= 50 ? 'orange' : 'red'}>
              {Math.round(record.completionPercent)}% complete
            </Tag>
            <Tag>{formatDuration(record.videoWatchTime)} watched</Tag>
          </div>
        </div>
      ),
    },
  ];

  return (
    <Drawer
      title={
        <div>
          <div style={{ fontSize: '16px', fontWeight: 600 }}>Share Analytics</div>
          {videoTitle && (
            <div style={{ fontSize: '13px', fontWeight: 'normal', color: '#666', marginTop: '4px' }}>
              {videoTitle}
            </div>
          )}
        </div>
      }
      placement="right"
      onClose={onClose}
      open={visible}
      width={isMobile ? '100%' : 720}
      destroyOnClose
    >
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <Spin size="large" />
          <p style={{ marginTop: '16px', color: '#666' }}>Loading analytics...</p>
        </div>
      ) : isError ? (
        <Empty
          description="Failed to load analytics"
          style={{ marginTop: '48px' }}
        />
      ) : !analytics || !analytics.summary ? (
        <Empty
          description="No analytics data available"
          style={{ marginTop: '48px' }}
        />
      ) : (
        <>
          {/* Summary Statistics */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Total Views"
                  value={analytics.summary.totalViews}
                  prefix={<EyeOutlined />}
                  valueStyle={{ fontSize: isMobile ? '20px' : '24px' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Unique Viewers"
                  value={analytics.summary.uniqueViewers}
                  prefix={<UserOutlined />}
                  valueStyle={{ fontSize: isMobile ? '20px' : '24px' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Avg Watch Time"
                  value={formatDuration(analytics.summary.avgVideoWatchTime)}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ fontSize: isMobile ? '16px' : '20px' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="Avg Completion"
                  value={Math.round(analytics.summary.avgCompletionPercent)}
                  suffix="%"
                  valueStyle={{ fontSize: isMobile ? '20px' : '24px' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Tabs for Timeline and Viewers */}
          <Tabs defaultActiveKey="viewers" size={isMobile ? 'small' : 'middle'}>
            <TabPane tab="Viewers" key="viewers">
              {analytics.viewers.length === 0 ? (
                <Empty
                  description="No viewers yet"
                  style={{ marginTop: '48px' }}
                />
              ) : (
                <Table
                  dataSource={analytics.viewers}
                  columns={isMobile ? mobileViewersColumns : viewersColumns}
                  rowKey={(record) => `${record.timestamp}-${record.viewerEmail}`}
                  pagination={{
                    pageSize: 10,
                    size: 'small',
                    showSizeChanger: false,
                  }}
                  scroll={isMobile ? undefined : { x: 800 }}
                  size="small"
                />
              )}
            </TabPane>

            <TabPane tab="Timeline" key="timeline">
              {analytics.timeline.length === 0 ? (
                <Empty
                  description="No timeline data yet"
                  style={{ marginTop: '48px' }}
                />
              ) : (
                <div style={{ padding: '16px' }}>
                  <h4 style={{ marginBottom: '16px' }}>Views Over Time</h4>
                  {analytics.timeline.map((point) => (
                    <div
                      key={point.date}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px',
                        marginBottom: '8px',
                        backgroundColor: '#f6f8fa',
                        borderRadius: '6px'
                      }}
                    >
                      <span style={{ fontWeight: 500 }}>
                        {new Date(point.date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                      <Tag color="blue">{point.views} view{point.views !== 1 ? 's' : ''}</Tag>
                    </div>
                  ))}
                </div>
              )}
            </TabPane>
          </Tabs>

          {/* Additional Info */}
          <div style={{
            marginTop: '24px',
            padding: '12px',
            backgroundColor: '#f0f9ff',
            borderRadius: '6px',
            fontSize: '12px',
            color: '#666'
          }}>
            <strong>Note:</strong> Analytics data is collected when viewers open the share link.
            Location data is based on IP address geolocation and may not always be accurate.
          </div>
        </>
      )}
    </Drawer>
  );
};

export default ShareAnalyticsDrawer;
